import { useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';

import { Button } from '../../../components/ui';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { LOGO_ACCEPT } from '../constants';
import type { ODuongAnh } from '../constants';
import { kiemTepLogo } from '../nghiep-vu/ho-so';

/**
 * Một ô ảnh nhận diện: khung xem trước, nút tải lên/thay, nút gỡ.
 *
 * Kiểm định dạng và dung lượng **trước khi gửi** để báo lỗi ngay cạnh ô; server
 * kiểm lại (magic bytes, kích thước). Khung xem trước dùng nền cố định theo ý
 * nghĩa của ô (`xem-truoc-nen-sang`/`xem-truoc-nen-toi`, ở `globals.css`) chứ
 * không theo chủ đề: ô "logo nền tối" phải cho thấy logo trên nền tối cả khi
 * giao diện đang sáng.
 */
export function OAnhHoSo({
  cau,
  url,
  dangXuLy,
  loiMay,
  onChon,
  onGo,
}: {
  cau: ODuongAnh;
  url: string | null;
  dangXuLy: boolean;
  /** Câu lỗi server trả về cho ô này, đã dịch. */
  loiMay?: string;
  onChon: (tep: File) => void;
  onGo: () => void;
}) {
  const { t } = useT();
  const input = useRef<HTMLInputElement>(null);
  const [loiTep, setLoiTep] = useState<KhoaDich | null>(null);
  const laFavicon = cau.slot === 'favicon';
  const nhan = t(cau.nhan);

  const khiChon = (e: ChangeEvent<HTMLInputElement>) => {
    const tep = e.target.files?.[0];
    // Xoá giá trị để chọn lại đúng tệp vừa bị từ chối vẫn kích hoạt `change`.
    e.target.value = '';
    if (!tep) return;
    const sai = kiemTepLogo(tep);
    setLoiTep(sai);
    if (!sai) onChon(tep);
  };

  const loi = loiTep ? t(loiTep) : loiMay;

  return (
    <section aria-label={nhan} className="space-y-2">
      <div>
        <h4 className="text-xs font-semibold text-content-primary">{nhan}</h4>
        <p className="text-2xs text-content-muted">{t(cau.mo)}</p>
      </div>

      <div
        className={cn(
          'flex h-16 items-center justify-center rounded-lg border border-default px-3',
          cau.nenToi ? 'xem-truoc-nen-toi' : 'xem-truoc-nen-sang',
        )}
      >
        {url ? (
          <img
            src={url}
            alt={nhan}
            className={cn('object-contain', laFavicon ? 'h-8 w-8' : 'max-h-12 max-w-full')}
          />
        ) : (
          <span className={cn('truncate text-2xs', cau.nenToi ? 'text-white' : 'text-content-muted')}>
            {t('hoSoToChuc.macDinh')}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={input}
          type="file"
          accept={LOGO_ACCEPT}
          className="hidden"
          tabIndex={-1}
          aria-hidden
          onChange={khiChon}
        />
        <Button
          variant="secondary"
          size="sm"
          icon={<UploadOutlined />}
          loading={dangXuLy}
          onClick={() => input.current?.click()}
        >
          {dangXuLy ? t('hoSoToChuc.dangTaiAnh') : url ? t('hoSoToChuc.thayAnh') : t('hoSoToChuc.taiAnhLen')}
        </Button>
        {url && (
          <Button variant="ghost" size="sm" icon={<DeleteOutlined />} disabled={dangXuLy} onClick={onGo}>
            {t('hoSoToChuc.goAnh')}
          </Button>
        )}
      </div>

      {loi && (
        <p role="alert" className="text-2xs text-danger-700">
          {loi}
        </p>
      )}
    </section>
  );
}
